import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '../../lib/api-client';
import { apiError } from '../../lib/utils';
import { useNotice } from '../ui/Notice';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';

interface SavedQuery { id: string; name: string; sql: string }

export function ListQueryBar({ resource, applied, onApply, onClear }: {
  resource: string;
  applied: string;
  onApply: (sql: string) => void;
  onClear: () => void;
}) {
  const notice = useNotice();
  const client = useQueryClient();
  const [sql, setSql] = useState(applied);
  const [picked, setPicked] = useState('');
  const [name, setName] = useState('');
  const [naming, setNaming] = useState(false);
  const saved = useQuery({
    queryKey: ['list-queries', resource],
    queryFn: () => apiGet<SavedQuery[]>(`/api/list-queries?resource=${encodeURIComponent(resource)}`),
  });

  useEffect(() => {
    setSql(applied);
    setPicked('');
    setNaming(false);
    setName('');
  }, [resource]);

  function pick(id: string) {
    setPicked(id);
    const row = (saved.data || []).find((item) => item.id === id);
    if (!row) return;
    setSql(row.sql);
    onApply(row.sql);
  }

  async function save(nextName: string) {
    const label = nextName.trim();
    if (!label || !sql.trim()) return;
    try {
      const row = await apiPost<SavedQuery>('/api/list-queries', { resource, name: label, sql });
      await client.invalidateQueries({ queryKey: ['list-queries', resource] });
      setPicked(row.id);
      setNaming(false);
      setName('');
      onApply(sql);
    } catch (err) {
      notice.failure(apiError(err, 'Could not save the query'));
    }
  }

  async function remove() {
    if (!picked) return;
    const ok = await notice.confirm('Delete query', 'This removes the saved query. The current list stays until you clear it.');
    if (!ok) return;
    try {
      await apiDelete(`/api/list-queries/${picked}`);
      await client.invalidateQueries({ queryKey: ['list-queries', resource] });
      setPicked('');
    } catch (err) {
      notice.failure(apiError(err, 'Could not delete the query'));
    }
  }

  const rows = saved.data || [];
  return (
    <div className="mb-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select className="max-w-[220px]" value={picked} onChange={(event) => pick(event.target.value)}>
          <option value="">Saved queries</option>
          {rows.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </Select>
        <span className="text-sm text-[var(--bo-muted)]">WHERE</span>
        <Input className="min-w-[16rem] flex-1" aria-label="List filter" placeholder={"Status = 'issued' AND Value >= 6000"} value={sql} onChange={(event) => setSql(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') onApply(sql); }} />
        <Button variant="outline" onClick={() => onApply(sql)}>Run</Button>
        <Button variant="outline" onClick={() => { const chosen = rows.find((row) => row.id === picked); if (chosen) void save(chosen.name); else setNaming(true); }}>Save</Button>
        {picked ? <Button variant="outline" onClick={() => void remove()}>Delete</Button> : null}
        {applied || sql.trim() ? <Button variant="outline" onClick={() => { setSql(''); setPicked(''); setNaming(false); onClear(); }}>Clear</Button> : null}
      </div>
      {naming ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input className="max-w-xs" aria-label="Query name" placeholder="Query name" value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void save(name); }} />
          <Button variant="outline" onClick={() => void save(name)}>Save query</Button>
        </div>
      ) : null}
      <p className="text-xs text-[var(--bo-muted)]">Write the filter only. The columns stay as they are. Quote a name that has a space. Money columns use pounds, so Value &gt;= 6000 is £6,000. Run replaces search, the customer list, the status list, and the tabs until you clear it. Save keeps this filter for this list.</p>
    </div>
  );
}
